import React, { useState, useEffect, useRef, forwardRef } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import toast from "react-hot-toast";
import { base_URL } from "../../../config/config";

const fetchMessages = async (enquiryId) => {
  const accessToken = localStorage.getItem("adminAccessToken");
  const response = await axios.get(`${base_URL}/api/message/get/${enquiryId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

const StatusDropdown = forwardRef(({ enquiry, updateStatus, user }, _ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const statusOptions = ["Proposal Sent", "Connected", "Booking Completed", "Lost"];

  const { data: messagesData, refetch, isFetching } = useQuery({
    queryKey: ["messages", enquiry._id],
    queryFn: () => fetchMessages(enquiry._id),
    enabled: false,
    staleTime: 5 * 60 * 1000,
  });

  const hasOurSideMessage = (data) => {
    if (!data) return false;
    const ourSenders = ["vendor", "admin"];
    const messages = data?.messages ?? [];
    for (const item of messages) {
      // Nested: { messages: [{ message: [{ sender: "vendor"|"admin" }] }] }
      if (Array.isArray(item?.message)) {
        if (item.message.some((msg) => ourSenders.includes(msg?.sender?.toLowerCase()))) return true;
      }
      // Flat: { messages: [{ sender: "vendor"|"admin" }] }
      if (ourSenders.includes(item?.sender?.toLowerCase())) return true;
    }
    return false;
  };

  const checkAndOpen = (data) => {
    if (hasOurSideMessage(data)) {
      setIsOpen(true);
    } else {
      toast("Connect with customer first to change the status", { icon: "💬" });
    }
  };

  const handleClick = async () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    if (messagesData !== undefined) {
      checkAndOpen(messagesData);
      return;
    }
    const result = await refetch();
    checkAndOpen(result.data ?? null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleStatusChange = (newStatus) => {
    updateStatus(enquiry?._id, newStatus, `${newStatus} by ${user?.email}`);
    setIsOpen(false);
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "Active":
      case "Connected":         return "bg-[#EAF9F7] text-[#039F8D]";
      case "Successful":
      case "Booking Completed": return "bg-[#DFDFFF] text-[#4A49D9]";
      case "Lost":              return "bg-[#EBEBEB] text-[#717171]";
      case "New":
      case "Not Accepted":
      case "Proposal Sent":     return "bg-[#E4EFFF] text-[#1252B1]";
      case "Venue Declined":    return "bg-[#FF3939]/20 text-[#FF3939]";
      default:                  return "bg-blue-50 text-blue-600";
    }
  };

  const getDisplayLabel = (status) => {
    switch (status) {
      case "Not Accepted":      return "New";
      case "Active":
      case "New":               return "Connected";
      case "Successful":        return "Booking Completed";
      case "Venue Declined":    return "venue declined";
      case "Customer Declined": return "client declined";
      default:                  return status;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`px-3 flex items-center justify-center gap-x-2 py-2 rounded-lg text-[14px] border-[0.5px] border-neutral-200 ${getStatusStyles(enquiry?.status)} ${
          isFetching ? "cursor-wait opacity-70" : "cursor-pointer"
        }`}
        onClick={handleClick}
      >
        {getDisplayLabel(enquiry?.status)}
        <span className="flex items-center justify-center">
          <img src="/downarrow.svg" alt="Arrow" />
        </span>
      </div>

      {isOpen && (
        <div className="absolute right-12 -top-16 bg-white shadow-lg rounded-lg z-10">
          {statusOptions.map((option) => (
            <div
              key={option}
              onClick={() => handleStatusChange(option)}
              className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                option === enquiry?.status ? "bg-gray-200 text-blue-600" : "text-gray-800"
              }`}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default StatusDropdown;
